import React, { useEffect } from "react";
import {
  Box,
  TextField,
  Grid,
  Autocomplete,
  useAutocomplete,
  Button,
  RefreshButton,
  Edit,
  List,
  DataGrid,
  esES,
} from "@pankod/refine-mui";
import { Controller, useForm } from "@pankod/refine-react-hook-form";
import { IAssembledProduct, IEmployee, IFinishedPieces } from "interfaces";

import { useModal, useNavigation, useTranslate } from "@pankod/refine-core";
import { useParams } from "react-router-dom";
import { FinishedPiecesModal } from "components/Modals/finished_pieces";
import { applyFinishedPiecesChange } from "components/Modals/finished_pieces/applyFinishedPiecesChange";
import { Add, AddOutlined, InventoryOutlined } from "@mui/icons-material";
import { finishedPiecesColumns, productColumns } from "./grid_columns";
import { useAssembledData } from "./hooks/useAssembledData";
import { v4 as uuid } from "uuid";
import _ from "lodash-es";
import { EnabledProductModal } from "./modals/products";
import { clientProductColumns } from "pages/order/grid_columns";
import { ClientProductsModal } from "components/Modals/client_products";

export const AssembledEdit: React.FC = () => {
  const params = useParams();
  const { id } = params as any;

  const [totalPiece, setTotalPiece] = React.useState(0);
  const [finishedPiece, setFinishedPiece] = React.useState<number>(0);
  const [realWeight, setRealWeight] = React.useState<number>();

  const [finishedPieces, setFinishedPieces] = React.useState<IFinishedPieces[]>(
    []
  );

  const [products, setProducts] = React.useState<IAssembledProduct[]>();
  const [currentProduct, setCurrentProduct] =
    React.useState<IAssembledProduct>();
  const [productsDeleted, setProductsDeleted] = React.useState<any[]>([]);
  const [productsAdd, setProductsAdd] = React.useState(false);

  const [prd, setPrd] = React.useState();
  const [decrease, setDecrease] = React.useState(0);

  const { list } = useNavigation();
  const translate = useTranslate();

  const {
    refineCore: { formLoading, onFinish, queryResult },
    saveButtonProps,
    control,
    getValues,
    formState: { errors },
    handleSubmit,
    // getValues,
  } = useForm({
    refineCoreProps: {
      resource: "assembles/edit",
      redirect: false,
      id: id,
      metaData: {
        populate: ["employee", "order.detail"],
      },
      onMutationSuccess: () => {
        list("orders/open");
      },
      successNotification: () => {
        return {
          description: translate("notifications.success"),
          message: translate("assembled.edit.success"),
          type: "success",
        };
      },
    },
  });

  const data = queryResult?.data?.data;

  const {
    products: productsData,
    isLoading,
    isFetched,
  } = useAssembledData({
    id: id,
    orderId: data?.order?.id,
  });

  useEffect(() => {
    if (isFetched) {
      setProducts(productsData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched]);

  const onSubmit = (newData: any) => {
    onFinish({
      ...newData,
      order: data?.order?.id,
      realWeight: realWeight,
      finishedPiece: finishedPiece,
      decrease: decrease,
      finishedPieces: finishedPieces,
      products: products,
      products_deleted: productsDeleted,
    });
  };

  const { autocompleteProps } = useAutocomplete<IEmployee>({
    resource: "employees",
    filters: [{ field: "active", operator: "eq", value: true }],
    defaultValue: data?.employee?.id,
    onSearch: (search) => [
      {
        field: "name",
        operator: "contains",
        value: search,
      },
    ],
  });

  const {
    show: showProductModal,
    close: closeProductModal,
    visible: visibleProductModal,
  } = useModal();

  const {
    show: showFinishedPiecesModal,
    close: closeFinishedPiecesModal,
    visible: visibleFinishedPiecesModal,
  } = useModal();

  const {
    show: showClientMaterialsModal,
    close: closeClientMaterialsModal,
    visible: visibleClientMaterialsModal,
  } = useModal();

  const handleAddProduct = (
    data: IAssembledProduct,
    another: boolean = false
  ) => {
    if (!another) {
      closeProductModal();
    }
    const currentProducts = products || [];

    if (data?.action === "create") {
      const productId = uuid();
      const newProduct = {
        id: productId,
        product: data.product,
        quantity_pieces: data.quantity_pieces,
        delete: handleDeleteProduct,
        update: handleUpdateProduct,
        action: data?.action,
      };
      setProducts([...currentProducts, newProduct]);
    } else if (data?.action === "update") {
      const newProducts = currentProducts.map((p) => {
        if (p.id === data.id) {
          return {
            ...p,
            product: data.product,
            quantity_pieces: data.quantity_pieces,
            action: _.includes(data?.id?.toString(), "-") ? "create" : "update",
          };
        }
        return p;
      });
      setProducts(newProducts);
      setCurrentProduct(undefined);
    }
  };

  const handleDeleteProduct = (id: string) => {
    const currentProducts = products || [];
    const newProducts = currentProducts.filter((p) => p.id !== id);
    setProducts(newProducts);
    if (!_.includes(id, "-")) {
      setProductsDeleted([...productsDeleted, id]);
    }
  };

  const handleUpdateProduct = (data: IAssembledProduct, canAdd: boolean) => {
    setCurrentProduct(data);
    setProductsAdd(canAdd);
    showProductModal();
  };

  const handleAddFinishedPieces = (newData: any) => {
    const result = applyFinishedPiecesChange({
      list: finishedPieces,
      newData,
      process: "Montado",
      orderId: data?.order?.id,
    });
    setFinishedPieces(result.list);
    setFinishedPiece(result.qty);
    setRealWeight(result.realWeight);
  };

  const getDecrease = () => {
    const prpData = prd ? prd : 0;
    const realWeightData = realWeight ? realWeight : 0;

    const decrease = prpData - realWeightData;
    setDecrease(+decrease.toFixed(2));
  };

  useEffect(() => {
    if (data) {
      setTotalPiece(data?.order?.total_pieces);
      setFinishedPiece(data?.finished_pieces);
      setRealWeight(data?.real_weight || 0);
      setPrd(data?.order?.detail?.real_weight || 0);
    }
  }, [data]);

  useEffect(() => {
    if (realWeight && prd) {
      getDecrease();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realWeight, prd]);

  const productsWithActions = products?.map((p) => ({
    ...p,
    delete: handleDeleteProduct,
    update: handleUpdateProduct,
    action: "none",
  }));

  return (
    <Edit
      isLoading={formLoading}
      saveButtonProps={{
        ...saveButtonProps,
        disabled: formLoading,
        onClick: handleSubmit(onSubmit),
      }}
      resource="assembled"
      headerButtons={() => {
        return (
          <>
            <RefreshButton
              resourceNameOrRouteName="assembled"
              recordItemId={id}
              metaData={{ populate: ["employee"] }}
            />
            <Button
              startIcon={<InventoryOutlined />}
              onClick={() => {
                showClientMaterialsModal();
              }}
            >
              Productos de cliente
            </Button>
          </>
        );
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Controller
              control={control}
              name="employee"
              rules={{ required: "El campo empleado es obligatorio." }}
              defaultValue={null as any}
              render={({ field }) => {
                return (
                  <Autocomplete
                    {...autocompleteProps}
                    {...field}
                    onChange={(_, value) => {
                      field.onChange(value);
                    }}
                    getOptionLabel={(item) => {
                      return (
                        autocompleteProps?.options?.find(
                          (p) => p?.id?.toString() === item?.id?.toString()
                        )?.name ?? item?.name ?? ""
                      );
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return (
                        value === undefined ||
                        option.id.toString() === value.id.toString()
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Empleado"
                        margin="normal"
                        variant="outlined"
                        error={!!errors.employee}
                        helperText={errors.employee?.message?.toString()}
                        required
                      />
                    )}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} />
          <Grid item xs={12} sm={6}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="finished_pieces"
                  label="Pz. Totales"
                  name="finished_pieces"
                  value={totalPiece || 0}
                  type="number"
                  className="readonly"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="finished_pieces"
                  label="Pz. Terminadas"
                  name="finished_pieces"
                  value={finishedPiece || 0}
                  type="number"
                  className="readonly"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="real_weight"
                  label="PRM"
                  name="real_weight"
                  value={realWeight || 0}
                  className="readonly"
                />
              </Grid>
              <Grid
                item
                xs={3}
                alignItems={"center"}
                justifyContent={"center"}
                display={"flex"}
              >
                <Button
                  color={"info"}
                  size={"small"}
                  onClick={() => showFinishedPiecesModal()}
                >
                  <AddOutlined /> Agregar Piezas
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              margin="normal"
              fullWidth
              id="prd"
              label="PRD"
              name="prd"
              value={prd || 0}
              className="readonly"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              margin="normal"
              fullWidth
              id="decrease"
              label="Merma"
              name="decrease"
              value={decrease || 0}
              className="readonly"
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <List
              breadcrumb={false}
              title={"Productos"}
              headerButtons={() => (
                <>
                  <Button
                    startIcon={<Add />}
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      showProductModal();
                    }}
                  >
                    Agregar
                  </Button>
                </>
              )}
            >
              <DataGrid
                rows={productsWithActions || []}
                hideFooter={true}
                autoHeight
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                columns={productColumns}
                loading={isLoading}
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": {
                    textOverflow: "clip",
                    whiteSpace: "break-spaces",
                    lineHeight: 1,
                  },
                }}
              />
            </List>
          </Grid>
          <EnabledProductModal
            visible={visibleProductModal}
            close={closeProductModal}
            title={"Nuevo producto"}
            setData={handleAddProduct}
            data={currentProduct}
            canAdd={productsAdd}
            setCanAdd={setProductsAdd}
          />
          <FinishedPiecesModal
            visible={visibleFinishedPiecesModal}
            close={closeFinishedPiecesModal}
            title={"Piezas terminadas"}
            data={{
              currentEmployee: getValues("employee"),
              orderQty: totalPiece || 0,
              currentQty: finishedPiece || 0,
              realWeight: realWeight,
            }}
            setData={handleAddFinishedPieces}
            history={{
              config: {
                filters: [
                  {
                    field: "process",
                    operator: "eq",
                    value: "Detallado",
                  },
                  {
                    field: "order",
                    operator: "" as any,
                    value: data?.order?.id,
                  },
                ],
              },
              metaData: {
                populate: ["employee"],
              },
              columns: finishedPiecesColumns,
              extraData: finishedPieces,
            }}
          />
          <ClientProductsModal
            visible={visibleClientMaterialsModal}
            close={closeClientMaterialsModal}
            title={"Productos del cliente"}
            data={{
              order: data?.order?.id,
            }}
            history={{
              config: {
                filters: [
                  {
                    field: "type",
                    operator: "eq",
                    value: "Entrada",
                  },
                  {
                    field: "order",
                    operator: "" as any,
                    value: data?.order?.id,
                  },
                ],
              },
              metaData: {
                populate: ["order", "product"],
              },
              resource: "products-data",
              columns: clientProductColumns,
            }}
          />
        </Grid>
      </Box>
    </Edit>
  );
};
